/**
 * External Resources Loader
 * Handles loading of external CSS and JavaScript resources asynchronously
 * to prevent render-blocking and improve page load performance
 */

/**
 * Load external CSS asynchronously
 * @param {string} href - CSS file URL
 * @param {string} media - Media query (default: 'all')
 * @returns {Promise} - Promise that resolves when CSS is loaded
 */
export function loadExternalCSS(href, media = 'all') {
  return new Promise((resolve, reject) => {
    // Check if CSS is already loaded
    const existingLink = document.querySelector(`link[href="${href}"]`);
    if (existingLink) {
      resolve(existingLink);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.media = media;
    
    link.onload = () => resolve(link);
    link.onerror = () => reject(new Error(`Failed to load external CSS: ${href}`));
    
    document.head.appendChild(link);
  });
}

/**
 * Load external JavaScript asynchronously
 * @param {string} src - JavaScript file URL
 * @returns {Promise} - Promise that resolves when script is loaded
 */
export function loadExternalJS(src) {
  return new Promise((resolve, reject) => {
    // Check if script is already loaded
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      resolve(existingScript);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    
    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error(`Failed to load external JS: ${src}`));
    
    document.head.appendChild(script);
  });
}

/**
 * Load all external CSS resources asynchronously
 * These are the external CSS files that were causing render-blocking
 */
export function loadExternalStyles() {
  const externalCSS = [
    // Google Fonts
    'https://fonts.googleapis.com/css?family=Open+Sans:300,400,600,700&display=swap',
    'https://fonts.googleapis.com/css2?family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap',
    
    // External icon libraries
    'https://cdnjs.cloudflare.com/ajax/libs/nvd3/1.8.1/nv.d3.min.css',
    'https://unpkg.com/ionicons@4.2.2/dist/css/ionicons.min.css',
    'https://maxst.icons8.com/vue-static/landings/line-awesome/line-awesome/1.3.0/css/line-awesome.min.css',
    
    // Slick carousel
    'https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css',
    'https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css'
  ];

  // Load all external CSS files
  const loadPromises = externalCSS.map(cssUrl => 
    loadExternalCSS(cssUrl).catch(error => {
      console.warn(`Failed to load external CSS: ${cssUrl}`, error);
    })
  );

  return Promise.all(loadPromises);
}

/**
 * Load external JavaScript resources asynchronously
 */
export function loadExternalScripts() {
  const externalJS = [
    // jQuery (if needed for compatibility)
    'https://code.jquery.com/jquery-3.6.0.min.js'
  ];

  // Load all external JavaScript files
  const loadPromises = externalJS.map(jsUrl => 
    loadExternalJS(jsUrl).catch(error => {
      console.warn(`Failed to load external JS: ${jsUrl}`, error);
    })
  );

  return Promise.all(loadPromises);
}

/**
 * Initialize external resource loading
 * Call this after the initial page render
 */
export function initializeExternalResources() {
  // Load external resources after a delay to not block initial render
  setTimeout(() => {
    Promise.all([
      loadExternalStyles(),
      loadExternalScripts()
    ]).then(() => {
      console.log('External resources loaded successfully');
    }).catch(error => {
      console.error('Error loading external resources:', error);
    });
  }, 100); // Small delay to ensure initial render is complete
}
